import React from 'react';

import AllIntegrations from '~/components/Admin/IntegrationPage/AllIntegrations/AllIntegrations';
import ApiManagement from '~/components/Admin/IntegrationPage/ApiManagement';
import ManageSyncPair from '~/components/Admin/IntegrationPage/ManageSyncPair/ManageSyncPair';
import ProcurementIntegrations from '~/components/Admin/IntegrationPage/ProcurementIntegrations/ProcurementIntegrations';
import SalesIntegrations from '~/components/Admin/IntegrationPage/SalesIntegrations/SalesIntegrations';
import StorageIntegrations from '~/components/Admin/IntegrationPage/StorageIntegrations/StorageIntegrations';
import ThirdPartyIntegrations from '~/components/Admin/IntegrationPage/ThirdPartyIntegrations/ThirdPartyIntegrations';
import { Tabs } from '~/eds/src/components/Tabs/Tabs';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Webhooks } from '~/features/webhooks/Webhooks';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { withCurrentUser } from '~/hocs/withCurrentUser';
import { useSearchParams } from '~/hooks/router';
import { useIntl } from 'react-intl';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { featureFlagIncluded } from '~/utils/user';

function Page({ currentUser }) {
  const intl = useIntl();
  const [tabValue, setTabValue] = useSearchParams('tab');
  const aribaIntegration = useFlag(FlagType.AribaControllerFlag);
  const webhooksIntegration = useFlag(FlagType.Webhooks);
  const multipleSFDCIntegrations = useFlag(
    FlagType.SupportMultipleSFDCOrganizations,
  );

  const allTabs = {
    ALL: {
      label: intl.formatMessage({
        id: 'IntegrationsDeprecated.allTab',
        defaultMessage: 'All',
        description: 'All integrations tab label',
      }),
      query: 'all',
      value: 'ALL',
      component: <AllIntegrations />,
    },
    THIRD_PARTY: {
      label: intl.formatMessage({
        id: 'IntegrationsDeprecated.thirdPartyTab',
        defaultMessage: '3rd Party Integrations',
        description: 'Third party integrations tab label',
      }),
      query: 'third-party',
      value: 'THIRD_PARTY',
      component: <ThirdPartyIntegrations />,
    },
    STORAGE: {
      label: intl.formatMessage({
        id: 'IntegrationsDeprecated.storageTab',
        defaultMessage: 'Storage',
        description: 'Storage integrations tab label',
      }),
      query: 'storage',
      value: 'STORAGE',
      component: <StorageIntegrations />,
    },
    PROCUREMENT: {
      label: intl.formatMessage({
        id: 'IntegrationsDeprecated.procurementTab',
        defaultMessage: 'Procurement',
        description: 'Procurement integrations tab label',
      }),
      query: 'procurement',
      value: 'PROCUREMENT',
      component: <ProcurementIntegrations />,
    },
    SALES: {
      label: intl.formatMessage({
        id: 'IntegrationsDeprecated.salesTab',
        defaultMessage: 'CRM / Sales',
        description: 'CRM/Sales integrations tab label',
      }),
      query: 'sales',
      value: 'SALES',
      component: <SalesIntegrations />,
    },
    API_MANAGEMENT: {
      label: intl.formatMessage({
        id: 'IntegrationsDeprecated.apiManagementTab',
        defaultMessage: 'API Management',
        description: 'API Management tab label',
      }),
      query: 'api-management',
      value: 'API_MANAGEMENT',
      component: <ApiManagement />,
    },
    WEBHOOKS: {
      label: intl.formatMessage({
        id: 'IntegrationsDeprecated.webhooksTab',
        defaultMessage: 'Webhooks',
        description: 'Webhooks tab label',
      }),
      query: 'webhooks',
      value: 'WEBHOOKS',
      component: <Webhooks />,
    },
  };

  if (!aribaIntegration) {
    delete allTabs.PROCUREMENT;
  }
  if (!webhooksIntegration) {
    delete allTabs.WEBHOOKS;
  }
  if (!multipleSFDCIntegrations) {
    delete allTabs.SALES;
  }

  const tabs = Object.values(allTabs).filter(
    ({ featureFlag }) =>
      !featureFlag || featureFlagIncluded(currentUser, featureFlag),
  );

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'IntegrationsDeprecated.navTitle',
        defaultMessage: 'Integrations',
        description: 'Navigation title for integrations page',
      }),
      pathname: RoutePathType.AdminConsoleClientIntegrations,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: currentUser.client,
    },
    intl,
  });

  function getSelectedTab() {
    const activeTab = tabs.find((tab) => tab.query === tabValue);
    const firstTab = tabs[0];
    return activeTab?.value || firstTab?.value;
  }

  function setSelectedTab(tab) {
    const value = allTabs[tab].query;
    setTabValue(value);
    if (tab !== 'webhooks') {
      const url = new URL(window.location);
      url.searchParams.delete('secondaryTab');
      window.history.pushState({}, '', url);
    }
  }

  const selectedTab = getSelectedTab();

  return (
    <>
      <PageLayout
        title={intl.formatMessage({
          id: 'IntegrationsDeprecated.pageTitle',
          defaultMessage: 'Integrations',
          description: 'Integrations page title',
        })}
        nav={nav}
      >
        <Tabs
          selectedTab={selectedTab}
          tabs={tabs}
          onSelectTab={setSelectedTab}
        />
      </PageLayout>
      {allTabs[selectedTab].component}
      <ManageSyncPair />
    </>
  );
}

export default withCurrentUser(Page);
